import { BarChart } from '@mui/icons-material';
import { HedgePlanTermReportModel } from '../../../../Generated/Commodity-Management-Api';
import { decileMatrixStyles, getDecileTextColor, readDecileColor } from '../DecileMatrixStyles';
import { MatrixBand, buildMatrixBands, findMatrixBand } from './HedgePlanMatrixBands';

export interface HedgePlanMatrixTableEntry {
  key: string;
  label: string;
  matrix: HedgePlanTermReportModel;
  lastClosePrice?: number;
  /** Price under the cursor on the chart, which takes precedence over the last close. */
  hoveredPrice?: number;
}

interface HedgePlanMatrixTableProps {
  entries: HedgePlanMatrixTableEntry[];
  /** Keys are `${entry.key}|${band.percentile}`, identifying which cells are plotted on the chart. */
  selectedCellKeys?: Set<string>;
  onCellClick?: (entry: HedgePlanMatrixTableEntry, band: MatrixBand) => void;
  /** Previews a band on the chart without pinning it; cleared by onCellHoverEnd. */
  onCellHover?: (entry: HedgePlanMatrixTableEntry, band: MatrixBand) => void;
  onCellHoverEnd?: () => void;
}

export default function HedgePlanMatrixTable(props: HedgePlanMatrixTableProps) {
  const { selectedCellKeys, onCellClick, onCellHover, onCellHoverEnd } = props;
  const entriesWithBands = props.entries
    .map((entry) => ({ ...entry, bands: buildMatrixBands(entry.matrix) }))
    .filter((entry) => entry.bands.length > 0);

  if (entriesWithBands.length === 0) {
    return null;
  }

  const percentiles = Array.from(new Set(entriesWithBands.flatMap((entry) => entry.bands.map((band) => band.percentile))))
    .sort((left, right) => right - left);
  const highlightedPercentileByEntryKey = new Map(
    entriesWithBands.map((entry) => {
      const highlightedBand = findMatrixBand(entry.bands, entry.hoveredPrice ?? entry.lastClosePrice);
      return [entry.key, highlightedBand?.percentile];
    }),
  );

  const columnWidth = 100;
  const tableWidth = (1 + entriesWithBands.length) * columnWidth;

  return (
    <div style={{ ...decileMatrixStyles.container, width: `${tableWidth}px`, boxSizing: 'content-box', flex: '0 0 auto', marginLeft: 'auto' }}>
      <table style={{ ...decileMatrixStyles.table, width: `${tableWidth}px`, tableLayout: 'fixed', boxSizing: 'border-box' }}>
        <thead>
          <tr>
            <th
              style={{
                ...decileMatrixStyles.headerCell,
                textAlign: 'left',
                width: `${columnWidth}px`,
                padding: '7px 6px',
              }}
            >
              Decile
            </th>
            {entriesWithBands.map((entry) => (
              <th
                key={entry.key}
                style={{
                  ...decileMatrixStyles.headerCell,
                  textAlign: 'right',
                  width: `${columnWidth}px`,
                  padding: '7px 6px',
                }}
              >
                {entry.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {percentiles.map((percentile) => {
            const rowLabel = entriesWithBands
              .flatMap((entry) => entry.bands)
              .find((band) => band.percentile === percentile)?.label;

            return (
              <tr key={percentile}>
                <td
                  style={{
                    ...decileMatrixStyles.labelCell,
                    backgroundColor: readDecileColor(percentile),
                    color: getDecileTextColor(percentile),
                    width: `${columnWidth}px`,
                    padding: '6px 6px',
                  }}
                >
                  {rowLabel}
                </td>
                {entriesWithBands.map((entry) => {
                  const band = entry.bands.find((candidate) => candidate.percentile === percentile);
                  const isHighlighted = highlightedPercentileByEntryKey.get(entry.key) === percentile;
                  const isSelected = band !== undefined && (selectedCellKeys?.has(`${entry.key}|${band.percentile}`) ?? false);

                  return (
                    <td
                      key={entry.key}
                      onClick={() => band && onCellClick?.(entry, band)}
                      onMouseEnter={() => band && onCellHover?.(entry, band)}
                      onMouseLeave={() => onCellHoverEnd?.()}
                      style={{
                        ...decileMatrixStyles.valueCell,
                        backgroundColor: isHighlighted ? readDecileColor(percentile) : 'transparent',
                        color: isHighlighted ? getDecileTextColor(percentile) : 'var(--font-color)',
                        cursor: band ? 'pointer' : 'default',
                        width: `${columnWidth}px`,
                        padding: '6px 6px',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        {isSelected && (
                          <BarChart style={{ fontSize: '16px', color: isHighlighted ? getDecileTextColor(percentile) : readDecileColor(percentile) }} />
                        )}
                        {band ? `${band.lowValue.toFixed(3)} - ${band.highValue.toFixed(3)}` : '\u2014'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--helper-text-color)' }}>
        Highlighted bands hold the last close. Hover a cell or the chart&apos;s price line to preview a band here and on the chart; click a cell to keep it.
      </p>
    </div>
  );
}
